// src/pages/About.jsx
// The dedicated author page.
// Carries the Colophon and The Stack from Module 7's homepage, plus a small
// "Currently" section listing present-tense preoccupations, a small magazine
// convention that fits the editorial voice well.

export default function About() {
  return (
    <>
      {/* ===========================
          PAGE HEADER
          =========================== */}
      <section className="max-w-wide mx-auto px-6 pt-20 pb-12 md:pt-28 md:pb-16">
        <p className="label-mono">Colophon</p>
        <h1 className="font-display text-h2 leading-tight font-medium mt-3">
          About the author
        </h1>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* ===========================
          BIO
          =========================== */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-3">
            <p className="label-mono">The Bio</p>
          </div>
          <div className="md:col-span-9 max-w-prose">
            <p className="font-display italic text-lede leading-relaxed text-ink mb-6">
              Sandra Hershey-Zagrans (<em>Alee</em>) is a full-stack developer
              in training, writing from a small operation in central Ohio.
            </p>
            <p className="text-body leading-relaxed text-ink-70 mb-5">
              The work here sits at the intersection of three older threads:
              nearly two decades in voice and network engineering, a working
              background in cybersecurity (CCNA), and a long-running interest in
              how systems (technical, civic, monetary) actually hold together.
              The bootcamp work is the through-line that makes those threads
              buildable.
            </p>
            <p className="text-body leading-relaxed text-ink-70">
              Recent preoccupations: PostgreSQL schema design, the EVM,
              fermentation, the Theory of Constraints, and what it would take to
              run a small manufacturing operation that respects both the math
              and the people doing the work.
            </p>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* ===========================
          THE STACK
          =========================== */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-3">
            <p className="label-mono">The Stack</p>
            <h2 className="font-display text-h2 leading-tight font-medium mt-3">
              Tools of the trade
            </h2>
          </div>
          <div className="md:col-span-9">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-8">
              <div>
                <dt className="label-mono mb-3">Frontend</dt>
                <dd className="text-body text-ink">
                  HTML, CSS, TailwindCSS, JavaScript, React
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Backend</dt>
                <dd className="text-body text-ink">
                  Node.js, Express, REST APIs
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Data</dt>
                <dd className="text-body text-ink">
                  PostgreSQL, MongoDB, parameterized SQL
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Web3 (in progress)</dt>
                <dd className="text-body text-ink">
                  Solidity, EVM fundamentals, Ethereum tooling
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Operations</dt>
                <dd className="text-body text-ink">
                  Linux, Docker, Git, CI workflows, networking
                </dd>
              </div>
              <div>
                <dt className="label-mono mb-3">Adjacent practice</dt>
                <dd className="text-body text-ink">
                  Technical writing, dev education video, systems thinking
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-wide mx-auto" />

      {/* ===========================
          CURRENTLY
          =========================== */}
      <section className="max-w-wide mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-3">
            <p className="label-mono">Currently</p>
            <h2 className="font-display text-h2 leading-tight font-medium mt-3">
              In rotation
            </h2>
          </div>
          <div className="md:col-span-9 max-w-prose">
            <dl className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 items-baseline">
                <dt className="label-mono md:col-span-3">Building</dt>
                <dd className="text-body text-ink md:col-span-9">
                  Footnote, the civic grounding layer.
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 items-baseline">
                <dt className="label-mono md:col-span-3">Reading</dt>
                <dd className="text-body text-ink md:col-span-9">
                  <em className="font-display italic">The Goal</em> by Eliyahu
                  Goldratt.
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 items-baseline">
                <dt className="label-mono md:col-span-3">Cooking</dt>
                <dd className="text-body text-ink md:col-span-9">
                  A pepper-heavy fermentation rotation.
                </dd>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-6 items-baseline">
                <dt className="label-mono md:col-span-3">Studying</dt>
                <dd className="text-body text-ink md:col-span-9">
                  Module 8 of the Metana bootcamp.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
    </>
  );
}
